import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { usePlayerContext, useStyles } from '../../../../../common/hooks';
import { getVolumeIcon } from '../../../../../common/utils/tools';
import { Button, Slider } from '../../../common';
import { VOLUME_BUTTON_SIZE, VOLUME_HIDDEN_STYLES, VOLUME_SLIDER_DEFAULT_DIALOG_STYLE, VOLUME_SLIDER_SIZE, VOLUME_VISIBLE_STYLES } from './styles';

import { TVShortcutController } from '../../../../../../core/shortcut/tvshortcut';
import { BACK_BTN_SHORTCUTS } from '../../../../../../core/shortcut/types';
import { UI_IS_INTERACTING } from '../../../../../../store/types';
import { USER_CLICK, USER_MUTE, USER_VOLUME_CHANGE } from '../../../../../../types';
import { SimpleTooltip, SIMPLETOOLTIP_VISIBILITY } from '../../../../../common/components/tooltips/SimpleTooltip';
import { BUTTON_LAYER } from '../../../../../common/constants';
import { useDialogContext, useRegisterDialog } from '../../../../../common/context/DialogContext';
import { OVERLAY_COLOR_OPAQUE } from '../../../../theme/colors';
import { RESIZE_FACTOR_EXP_CTRL_RESPONSIVE, VOLUME_DIALOG_LAYER } from '../../../../theme/constants';
import { withBreakPoints } from '../../../../hoc';

function Volume({
  volume,
  muted,
  mobile,
  medium,
  extraSmall,
  small,
  name,
  disableFocus,
  ...extraProps
}) {
  const resolveStateToSliderVal = (vol) => Math.ceil((100 * vol)).toFixed(2);

  const player = usePlayerContext();
  const dialogCtx = useDialogContext();
  const activeIconRef = useRef(null);
  const { store } = usePlayerContext();
  const [volumeState, setVolumeState] = useState(volume);
  const [sliderVal, setSliderVal] = useState(resolveStateToSliderVal(volumeState));
  const [sliding, setSliding] = useState(false);
  const [opened, setOpened] = useState(false);
  const [tooltipVisibility, setTooltipVisibility] = useState(SIMPLETOOLTIP_VISIBILITY.alwaysHidden);
  const setStyles = useStyles();

  useRegisterDialog({
    id: name, openingMode: 'over', state: [opened, setOpened]
  });

  useEffect(() => !sliding && setVolumeState(volume), [volume]);

  useEffect(() => {
    setSliderVal(resolveStateToSliderVal(volumeState));

    if (volumeState !== volume) player.volume(volumeState);
  }, [volumeState]);

  useEffect(() => (sliding && muted && player.mute(false)), [sliding, muted]);

  const toggleMute = () => {
    player.userEvents$.next({ action: USER_MUTE, value: !muted, source: USER_CLICK });
    player.mute(!muted);
  };

  const resizeFactor = (medium || small ? RESIZE_FACTOR_EXP_CTRL_RESPONSIVE : 1);
  const miniResizeFactor = resizeFactor / (small ? 1.80 : 1);

  const size = setStyles(VOLUME_SLIDER_SIZE);

  const handleSlide = (pos, maxPos) => {
    const val = pos / maxPos;

    setVolumeState(parseFloat(val.toFixed(2)));
  };

  const handleStopSlide = (pos, maxPos) => {
    player.userEvents$.next({ action: USER_VOLUME_CHANGE, source: USER_CLICK, value: parseFloat((pos / maxPos).toFixed(2)) });
    setSliding(false);
  };

  const [sliderDynamicStyles, setSliderDynamicStyles] = useState(VOLUME_HIDDEN_STYLES);
  const dialogContainerRef = useRef(null);

  useEffect(() => {
    store.dispatch({ type: UI_IS_INTERACTING, payload: { isInteracting: opened } });
    setSliderDynamicStyles(opened ? VOLUME_VISIBLE_STYLES : VOLUME_HIDDEN_STYLES);
  }, [opened]);

  useEffect(() => {
    if (mobile) setTooltipVisibility(SIMPLETOOLTIP_VISIBILITY.alwaysHidden);
    else setTooltipVisibility(opened ? SIMPLETOOLTIP_VISIBILITY.alwaysVisible : SIMPLETOOLTIP_VISIBILITY.default);
  }, [opened, mobile]);

  const onFocusSlider = (e) => {
    if (dialogContainerRef.current && dialogContainerRef.current.contains(e.target)) {
      setOpened(true);
    }
  };

  const onBlurContainer = () => {
    setTimeout(() => {
      if (dialogContainerRef.current && !dialogContainerRef.current.contains(document.activeElement)) {
        setOpened(false);
      }
    }, 0);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={dialogContainerRef}
      style={{ position: 'relative', display: 'flex' }}
      onBlur={onBlurContainer}
      onMouseEnter={() => {
        setOpened(true);
      }}
      onMouseLeave={() => {
        setOpened(false);
      }}
      onKeyDown={(e) => {
        if (dialogCtx.dialogOpened && TVShortcutController.getPrioritizedKeyCode(e, [BACK_BTN_SHORTCUTS])) {
          e.preventDefault();
          dialogCtx.closeAll();
          activeIconRef?.current?.focus();
        }
      }}
    >
      <Button
        ref={activeIconRef}
        type="icon"
        label={!opened ? 'Son (m)' : ''}
        forceLabel
        styles={{
          ...(!extraSmall && opened && {
            subWrapperStyle: {
              background: OVERLAY_COLOR_OPAQUE, borderRadius: '0 0 30px 30px'
            }
          })
        }}
        onClick={toggleMute}
        icon={getVolumeIcon({ volume, muted })}
        openingMode="over"
        xs
        name={name}
        disableFocus={disableFocus}
        zIndex={BUTTON_LAYER + 1}
        size={setStyles(VOLUME_BUTTON_SIZE).width}
        hideLabel={opened}
        {...extraProps}
      />
      <div
        className="volume-slider-container"
        style={{
          ...VOLUME_SLIDER_DEFAULT_DIALOG_STYLE,
          ...sliderDynamicStyles,
          zIndex: BUTTON_LAYER + 1
        }}
      >
        <SimpleTooltip
          content="Son (m)"
          position="top"
          visibility={tooltipVisibility}
        />
        <Slider
          value={muted ? 0 : sliderVal}
          strokeSize={6}
          cursorSize={size.cursorSize}
          trackSize={size.trackSize}
          onFocus={onFocusSlider}
          onMouseEnter={onFocusSlider}
          onBlur={() => setOpened(false)}
          onMouseLeave={() => setOpened(false)}
          onSlide={handleSlide}
          onStartSlide={() => setSliding(true)}
          onStopSlide={handleStopSlide}
          styles={{
            container: {
              paddingTop: 20 * resizeFactor,
              marginBottom: (size.cursorSize / 2) * miniResizeFactor,
              zIndex: VOLUME_DIALOG_LAYER,
              width: '100%'
            },
            outerTrack: {
              width: '100%'
            }
          }}
          vertical
          name="slider-volume"
          accessibleName="volume"
          disableFocus={disableFocus}
          showSlider={!extraSmall}
        />
      </div>
    </div>
  );
}

export default withBreakPoints(Volume);
